/**
 * Files API Bridge
 *
 * Client-side API for file search operations over Nest.
 */

// ============================================================================
// Type Definitions
// Nest descriptor-generated types are the contract for migrated call sites.
// ============================================================================

export interface FsWorkspaceContext {
    workspaceId: string
    workspaceRoot: string
    worktreeId?: string
}

export interface FuzzySearchParams {
    dir: string
    query: string
    matchDirs: boolean
    limit?: number
    context?: FsWorkspaceContext
}

export interface TreeChild {
    name: string
    isDir: boolean
    fullPath: string
}

export interface TreeMatch {
    path: string
    children: TreeChild[]
}

export interface FuzzySearchResponse {
    results: string[]
    untrackedResults: string[]
    truncated: boolean
    source: "git" | "ripgrep" | "fs"
    treeMatch?: TreeMatch
}

// ============================================================================
// describePath Types
// ============================================================================

interface DescribePathParams {
    path: string
    readContents?: boolean
    maxReadSize?: number
    showHidden?: boolean
    context?: FsWorkspaceContext
}

interface DescribeHostPathParams {
    path: string
    readContents?: boolean
    maxReadSize?: number
    showHidden?: boolean
}

/**
 * Files reachable from a task are either inside its workspace root, where reads
 * stay pathscope-jailed, or anywhere else on the node, where reads use the
 * node-scoped host surface.
 */
export type FileLocation = { kind: "workspace"; path: string; context: FsWorkspaceContext } | { kind: "host"; path: string }

export interface PathEntry {
    name: string
    path: string
    isDir: boolean
    isSymlink: boolean
    kind?: string
    symlinkTarget?: string
    size: number
    mode: number
    modeText?: string
    modifiedAt?: string
}

export type DescribePathResponse =
    | {
          type: "dir"
          path: string
          mode: number
          modeText?: string
          modifiedAt?: string
          kind?: string
          isSymlink?: boolean
          symlinkTarget?: string
          truncated?: boolean
          entries: PathEntry[]
      }
    | {
          type: "file"
          path: string
          size: number
          mode: number
          modeText?: string
          modifiedAt?: string
          kind?: string
          isSymlink?: boolean
          symlinkTarget?: string
          content: string | null
          tooLarge: boolean
          isReadable: boolean
          isBinary?: boolean
          mediaType?: string | null
          previewKind?: "image" | "pdf" | null
      }
    | { type: "not_found"; path: string }
    | { type: "error"; path: string; message: string }

// ============================================================================
// Content Search Types
// ============================================================================

interface ContentSearchParams {
    dir: string
    query: string
    limit?: number // default 100
    caseSensitive?: boolean
    regex?: boolean
    rankByHotFiles?: boolean // if true, rank results by git commit frequency
    context?: FsWorkspaceContext
}

export interface ContentSearchMatch {
    file: string // relative path from dir
    line: number // 1-indexed
    content: string // full line content
    matchStart: number // character offset in content where match starts
    matchEnd: number // character offset in content where match ends
}

interface ContentSearchResponse {
    matches: ContentSearchMatch[]
    truncated: boolean
}

// ============================================================================
// API Check
// ============================================================================

import { isCodeModuleAvailable } from "./capabilities"
import { isNestApiAvailable, requestNestMethod } from "../nest/client"
import { type NestFileBytes, downloadNestFileBytes } from "../nest/fileBytes"
import type { FsDescribeResult, HostFile } from "../nest/generated"

function normalizePath(path: string): string {
    return path.replace(/\\/g, "/").replace(/\/+/g, "/")
}

function trimTrailingSlash(path: string): string {
    const normalized = normalizePath(path)
    return normalized.length > 1 ? normalized.replace(/\/+$/g, "") : normalized
}

function absoluteFromRelative(root: string, relativePath: string): string {
    const normalizedRoot = trimTrailingSlash(root)
    const normalizedRelative = normalizePath(relativePath).replace(/^\.\//, "")
    if (!normalizedRelative || normalizedRelative === ".") return normalizedRoot
    return `${normalizedRoot}/${normalizedRelative}`
}

function relativeFromAbsolute(root: string, path: string): string {
    const normalizedRoot = trimTrailingSlash(root)
    const normalizedPath = trimTrailingSlash(path)
    if (normalizedPath === normalizedRoot) return "."
    const prefix = `${normalizedRoot}/`
    if (normalizedPath.startsWith(prefix)) return normalizedPath.slice(prefix.length)
    throw new Error(`Path is outside workspace root: ${path}`)
}

function requireFsContext(context: FsWorkspaceContext | undefined): FsWorkspaceContext {
    if (!isNestApiAvailable()) throw new Error("Nest API is required")
    if (!context?.workspaceId || !context.workspaceRoot) {
        throw new Error("Nest filesystem calls require workspace context")
    }
    return context
}

function nestFsParamsForPath(path: string, context: FsWorkspaceContext): { workspaceId: string; path: string; worktreeId?: string } {
    return {
        workspaceId: context.workspaceId,
        path: relativeFromAbsolute(context.workspaceRoot, path),
        ...(context.worktreeId ? { worktreeId: context.worktreeId } : {}),
    }
}

function isImageMediaType(mediaType: string | undefined): boolean {
    return mediaType?.startsWith("image/") ?? false
}

function previewKindForMediaType(mediaType: string | undefined): "image" | "pdf" | null {
    if (isImageMediaType(mediaType)) return "image"
    return mediaType?.split(";", 1)[0]?.trim().toLowerCase() === "application/pdf" ? "pdf" : null
}

function toDescribePathResponse(result: FsDescribeResult, inputPath: string, context: FsWorkspaceContext, readContents: boolean): DescribePathResponse {
    const absolutePath = result.exists ? absoluteFromRelative(context.workspaceRoot, result.path) : inputPath
    if (!result.exists) {
        return { type: "not_found", path: absolutePath }
    }
    if (result.isDir) {
        return {
            type: "dir",
            path: absolutePath,
            mode: result.modeBits ?? 0,
            ...(result.mode ? { modeText: result.mode } : {}),
            ...(result.modifiedAt ? { modifiedAt: result.modifiedAt } : {}),
            ...(result.kind ? { kind: result.kind } : {}),
            ...(result.isSymlink !== undefined ? { isSymlink: result.isSymlink } : {}),
            ...(result.symlinkTarget ? { symlinkTarget: result.symlinkTarget } : {}),
            ...(result.childrenTruncated !== undefined || result.truncated !== undefined
                ? { truncated: result.childrenTruncated ?? result.truncated ?? false }
                : {}),
            entries: (result.children ?? []).map((entry) => ({
                name: entry.name,
                path: absoluteFromRelative(context.workspaceRoot, entry.path),
                isDir: entry.isDir,
                isSymlink: entry.isSymlink ?? false,
                ...(entry.kind ? { kind: entry.kind } : {}),
                ...(entry.symlinkTarget ? { symlinkTarget: entry.symlinkTarget } : {}),
                size: entry.sizeBytes,
                mode: entry.modeBits ?? 0,
                ...(entry.mode ? { modeText: entry.mode } : {}),
                ...(entry.modifiedAt ? { modifiedAt: entry.modifiedAt } : {}),
            })),
        }
    }
    const isBinary = result.binary ?? false
    return {
        type: "file",
        path: absolutePath,
        size: result.sizeBytes,
        mode: result.modeBits ?? 0,
        ...(result.mode ? { modeText: result.mode } : {}),
        ...(result.modifiedAt ? { modifiedAt: result.modifiedAt } : {}),
        ...(result.kind ? { kind: result.kind } : {}),
        ...(result.isSymlink !== undefined ? { isSymlink: result.isSymlink } : {}),
        ...(result.symlinkTarget ? { symlinkTarget: result.symlinkTarget } : {}),
        content: readContents && !isBinary ? (result.content ?? "") : null,
        tooLarge: result.truncated ?? false,
        isReadable: true,
        isBinary,
        mediaType: result.mediaType ?? null,
        previewKind: previewKindForMediaType(result.mediaType),
    }
}

function toHostDescribePathResponse(result: HostFile, readContents: boolean): DescribePathResponse {
    if (!result.exists) {
        return { type: "not_found", path: result.path }
    }
    if (result.isDir) {
        return {
            type: "dir",
            path: result.path,
            mode: result.modeBits ?? 0,
            ...(result.mode ? { modeText: result.mode } : {}),
            ...(result.modifiedAt ? { modifiedAt: result.modifiedAt } : {}),
            ...(result.kind ? { kind: result.kind } : {}),
            ...(result.isSymlink !== undefined ? { isSymlink: result.isSymlink } : {}),
            ...(result.symlinkTarget ? { symlinkTarget: result.symlinkTarget } : {}),
            ...(result.childrenTruncated !== undefined || result.truncated !== undefined
                ? { truncated: result.childrenTruncated ?? result.truncated ?? false }
                : {}),
            entries: (result.children ?? []).map((entry) => ({
                name: entry.name,
                path: entry.path,
                isDir: entry.isDir,
                isSymlink: entry.isSymlink ?? false,
                ...(entry.kind ? { kind: entry.kind } : {}),
                ...(entry.symlinkTarget ? { symlinkTarget: entry.symlinkTarget } : {}),
                size: entry.sizeBytes,
                mode: entry.modeBits ?? 0,
                ...(entry.mode ? { modeText: entry.mode } : {}),
                ...(entry.modifiedAt ? { modifiedAt: entry.modifiedAt } : {}),
            })),
        }
    }
    const isBinary = result.binary ?? false
    return {
        type: "file",
        path: result.path,
        size: result.sizeBytes,
        mode: result.modeBits ?? 0,
        ...(result.mode ? { modeText: result.mode } : {}),
        ...(result.modifiedAt ? { modifiedAt: result.modifiedAt } : {}),
        ...(result.kind ? { kind: result.kind } : {}),
        ...(result.isSymlink !== undefined ? { isSymlink: result.isSymlink } : {}),
        ...(result.symlinkTarget ? { symlinkTarget: result.symlinkTarget } : {}),
        content: readContents && !isBinary ? (result.content ?? "") : null,
        tooLarge: result.truncated ?? false,
        isReadable: true,
        isBinary,
        mediaType: result.mediaType ?? null,
        previewKind: previewKindForMediaType(result.mediaType),
    }
}

function fileBytesQuery(location: FileLocation): { workspaceId?: string; worktreeId?: string; path?: string; hostPath?: string } {
    if (location.kind === "host") return { hostPath: location.path }
    return nestFsParamsForPath(location.path, requireFsContext(location.context))
}

// ============================================================================
// Files API Functions
// ============================================================================

/**
 * Fuzzy search for files or directories in a given directory
 *
 * Uses the best available method:
 * 1. git ls-files (if in a git repo)
 * 2. ripgrep (if available in PATH or vendored)
 * 3. filesystem walk (fallback)
 */
export async function fuzzySearch(params: FuzzySearchParams): Promise<FuzzySearchResponse> {
    const context = requireFsContext(params.context)
    const response = await requestNestMethod("fs.search.files", {
        ...nestFsParamsForPath(params.dir, context),
        query: params.query,
        limit: params.limit,
        includeDirectories: params.matchDirs,
    })
    return {
        results: params.matchDirs ? (response.directories ?? []) : response.files,
        untrackedResults: params.matchDirs ? [] : (response.untrackedFiles ?? []),
        truncated: response.truncated,
        source: "fs",
    }
}

async function describePath(params: DescribePathParams): Promise<DescribePathResponse> {
    const context = requireFsContext(params.context)
    const response = await requestNestMethod("fs.describe", {
        ...nestFsParamsForPath(params.path, context),
        readContent: params.readContents ?? false,
        maxBytes: params.maxReadSize,
        showHidden: params.showHidden ?? false,
    })
    return toDescribePathResponse(response, params.path, context, params.readContents ?? false)
}

async function contentSearch(params: ContentSearchParams): Promise<ContentSearchResponse> {
    const context = requireFsContext(params.context)
    const response = await requestNestMethod("fs.search.content", {
        ...nestFsParamsForPath(params.dir, context),
        query: params.query,
        limit: params.limit,
        caseSensitive: params.caseSensitive ?? false,
        regex: params.regex ?? false,
    })
    return {
        matches: response.matches.map((match) => {
            const [matchStart, matchEnd] = byteOffsetsToStringRange(match.lineText, match.matchStart, match.matchEnd)
            return {
                file: match.path,
                line: match.lineNumber ?? match.line ?? 1,
                content: match.lineText,
                matchStart,
                matchEnd,
            }
        }),
        truncated: response.truncated,
    }
}

const utf8Encoder = new TextEncoder()
const utf8Decoder = new TextDecoder()

function byteOffsetsToStringRange(value: string, start: number | undefined, end: number | undefined): [number, number] {
    const bytes = utf8Encoder.encode(value)
    const startByte = Math.max(0, Math.min(start ?? 0, bytes.length))
    const endByte = Math.max(startByte, Math.min(end ?? bytes.length, bytes.length))
    return [utf8Decoder.decode(bytes.subarray(0, startByte)).length, utf8Decoder.decode(bytes.subarray(0, endByte)).length]
}

async function describeHostPath(params: DescribeHostPathParams): Promise<DescribePathResponse> {
    if (!isNestApiAvailable()) throw new Error("Nest API is required")
    const result = await requestNestMethod("host.file.describe", {
        path: params.path,
        readContent: params.readContents ?? false,
        maxBytes: params.maxReadSize,
        showHidden: params.showHidden ?? true,
    })
    return toHostDescribePathResponse(result, params.readContents ?? false)
}

/**
 * Reads raw bytes over Nest HTTP so previews never travel as base64 on the
 * JSON-RPC socket and stay identical on Electron, browser, and mobile.
 */
async function readFileBytes(
    location: FileLocation,
    options: { signal?: AbortSignal; range?: { start: number; endExclusive: number } } = {}
): Promise<NestFileBytes> {
    if (!isNestApiAvailable()) throw new Error("Nest API is required")
    return downloadNestFileBytes(fileBytesQuery(location), options)
}

export function isFilesApiAvailable(): boolean {
    return isCodeModuleAvailable() && isNestApiAvailable()
}

export const filesApi = {
    fuzzySearch,
    describePath,
    describeHostPath,
    contentSearch,
    readFileBytes,
    isAvailable: isFilesApiAvailable,
}
